import React, { useState, useEffect } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import { api } from '../../services/api';
import { useToast } from '../hooks/useToast';
import { categories as dummyCategories } from '../data/dummyData';

const Categories = () => {
  const { success, error: showError, warning } = useToast();
  const [categoryList, setCategoryList] = useState(dummyCategories);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        setError('');

        try {
          const response = await api.get('/categories');
          const apiCategories = (response.data?.data || []).map((cat) => ({
            id: cat._id,
            name: cat.name,
            description: cat.description || '',
            productCount: 0,
            image: cat.image || '',
            parent: cat.parent || null,
            createdAt: new Date(cat.createdAt).toLocaleDateString(),
          }));
          setCategoryList(apiCategories);
        } catch (apiErr) {
          // Fall back to dummy data if API fails
          setCategoryList(dummyCategories);
          if (apiErr.response?.status !== 404) {
            warning('Using sample data - API not available');
          }
        }
      } catch (err) {
        const msg = err.response?.data?.message || 'Failed to load categories';
        setError(msg);
        warning(msg);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, [warning]);

  const handleDelete = (id) => {
    setSelectedCategory(id);
    setConfirmDelete(true);
  };

  const confirmDeleteCategory = async () => {
    try {
      setDeleting(true);
      await api.patch(`/categories/${selectedCategory}`, { deleted: true });
      setCategoryList((current) => current.filter((cat) => cat.id !== selectedCategory));
      success('Category deleted successfully');
    } catch (err) {
      const msg = err.response?.data?.message || 'Unable to delete category';
      showError(msg);
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { 
      key: 'name', 
      label: 'Category Name', 
      sortable: true,
      render: (val, row) => (
        <div>
          <p className="font-medium text-gray-900">{val}</p>
          {row.description && <p className="text-xs text-gray-500">{row.description.substring(0, 50)}...</p>}
        </div>
      )
    },
    { key: 'productCount', label: 'Products', sortable: true },
    {
      key: 'createdAt',
      label: 'Added',
      sortable: true,
      render: (val) => new Date(val).toLocaleDateString(),
    },
    {
      key: 'id',
      label: 'Status',
      render: () => (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
          Active
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title="Categories"
          description="Manage product categories"
          breadcrumbs={['Home', 'Categories']}
          action={
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition font-medium"
            >
              <Plus size={18} />
              Add Category
            </motion.button>
          }
        />

        {error && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-xl border border-gray-100 p-8 flex items-center justify-center">
            <Loader2 className="animate-spin text-amber-600 mr-2" size={20} />
            <span className="text-gray-600">Loading categories...</span>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={categoryList}
            onDelete={handleDelete}
            searchable={true}
            paginate={true}
            itemsPerPage={10}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={confirmDeleteCategory}
        title="Delete Category"
        message="Are you sure you want to delete this category?"
        confirmText={deleting ? "Deleting..." : "Delete"}
        isDangerous={true}
      />
    </>
  );
};

export default Categories;
